import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Users, CheckCircle, Clock, TrendingUp, Calendar, Eye, FileText, BarChart3, Inbox, Share2, Copy, Zap, ArrowLeft, HelpCircle } from 'lucide-react';
import ResponseChart from '../shared/charts/ResponseChart';
import MetricCard from '../shared/MetricCard';
import { getFormAnalytics } from '../../../services/responseService';
import formService from '../../../services/formService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../common/LoadingSpinner';
import FormAnalyticsTour from '../FormAnalyticsTour';

// Helper function to render answers based on type
const renderAnswer = (answer, questionType) => {
  if (answer === null || answer === undefined || answer === '') {
    return <span className="text-gray-400 italic">No answer provided</span>;
  }

  // Handle image type questions
  if (questionType === 'image' && typeof answer === 'string') {
    // Check if answer is a URL
    if (answer.startsWith('http://') || answer.startsWith('https://')) {
      return (
        <div className="mt-2">
          <img
            src={answer}
            alt="User uploaded response"
            className="max-w-full h-auto rounded-lg border-2 border-gray-200 shadow-md max-h-96 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage unavailable%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
      );
    }
  }

  // Handle array answers (checkboxes, ranking)
  if (Array.isArray(answer)) {
    if (questionType === 'ranking') {
      return (
        <div className="flex flex-col gap-2">
          {answer.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                {idx + 1}
              </span>
              <span className="text-base text-gray-900">{item}</span>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {answer.map((item, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 border border-blue-200"
          >
            {item}
          </span>
        ))}
      </div>
    );
  }

  // Handle rating (1-5 stars)
  if (questionType === 'rating') {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={`text-2xl ${i < answer ? 'text-yellow-400' : 'text-gray-300'}`}>
            ★
          </span>
        ))}
        <span className="ml-2 text-sm font-semibold text-gray-700">({answer}/5)</span>
      </div>
    );
  }

  // Handle scale (1-10 linear scale)
  if (questionType === 'scale') {
    return (
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-200 rounded-full h-3 max-w-xs">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all"
            style={{ width: `${(answer / 10) * 100}%` }}
          />
        </div>
        <span className="text-lg font-bold text-blue-600">{answer}/10</span>
      </div>
    );
  }

  // Handle yes/no
  if (questionType === 'yes-no') {
    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold ${
        answer === 'Yes' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'
      }`}>
        {answer}
      </span>
    );
  }

  // Handle gender
  if (questionType === 'gender') {
    return (
      <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold bg-purple-100 text-purple-700 border border-purple-200">
        {answer}
      </span>
    );
  }

  return <p className="text-base text-gray-900 leading-relaxed">{answer.toString()}</p>;
};

// Helper function to calculate time ago
const getTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Helper function to render question-specific analytics
const renderQuestionAnalytics = (question, analytics, totalResponses) => {
  const { type } = question;

  // Multiple Choice - Show bar chart
  if (type === 'multiple-choice') {
    const answerDist = analytics.answerDistribution || analytics.optionCounts || {};
    
    if (answerDist && Object.keys(answerDist).length > 0) {
      const sortedAnswers = Object.entries(answerDist)
        .sort(([, a], [, b]) => b - a);
      const maxCount = Math.max(...Object.values(answerDist));

      return (
        <div className="space-y-3 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto pr-2">
          {sortedAnswers.map(([answer, count]) => {
            const percentage = (count / analytics.totalAnswers) * 100;
            const barWidth = (count / maxCount) * 100;

            return (
              <div key={answer} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700 flex-1 truncate">{answer}</span>
                  <div className="flex items-center gap-3 ml-4">
                    <span className="font-bold text-gray-900">{count}</span>
                    <span className="text-gray-500 min-w-[3rem] text-right">{percentage.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500 flex items-center px-3"
                    style={{ width: `${barWidth}%` }}
                  >
                    {barWidth > 15 && (
                      <span className="text-xs font-semibold text-white">{count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback when no answer distribution data
    return (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm text-gray-600">
          {analytics.totalAnswers > 0
            ? `${analytics.totalAnswers} responses collected - distribution data unavailable`
            : 'No responses collected yet.'}
        </p>
      </div>
    );
  }

  // Checkbox - Show multiple selection bar chart
  if (type === 'checkbox') {
    if (analytics.answerDistribution && Object.keys(analytics.answerDistribution).length > 0) {
      const sortedAnswers = Object.entries(analytics.answerDistribution)
        .sort(([, a], [, b]) => b - a);
      const maxCount = Math.max(...Object.values(analytics.answerDistribution));

      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-3">
            <span className="font-semibold">Note:</span> Multiple selections allowed
          </p>
          <div className="max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto pr-2 space-y-3">
            {sortedAnswers.map(([answer, count]) => {
              const percentage = (count / analytics.totalAnswers) * 100;
              const barWidth = (count / maxCount) * 100;

              return (
                <div key={answer} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700 flex-1 truncate">{answer}</span>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="font-bold text-gray-900">{count}</span>
                      <span className="text-gray-500 min-w-[3rem] text-right">{percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-600 to-green-400 transition-all duration-500 flex items-center px-3"
                      style={{ width: `${barWidth}%` }}
                    >
                      {barWidth > 15 && (
                        <span className="text-xs font-semibold text-white">{count}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Fallback when no answer distribution data
    return (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm text-gray-600">
          {analytics.totalAnswers > 0
            ? `${analytics.totalAnswers} responses collected - distribution data unavailable`
            : 'No responses collected yet.'}
        </p>
      </div>
    );
  }

  // Rating - Show star average and distribution
  if (type === 'rating' && analytics.avgRating !== undefined) {
    const avgRating = analytics.avgRating;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200">
          <div className="text-center">
            <div className="text-4xl font-black text-yellow-600 mb-1">
              {avgRating.toFixed(1)}
            </div>
            <div className="flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={`text-xl ${i < Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'}`}>
                  ★
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-1">Average Rating</p>
          </div>
          <div className="flex-1 text-sm text-gray-700">
            <p className="font-medium mb-1">Based on {analytics.totalAnswers} responses</p>
            <p className="text-gray-600">Overall satisfaction: {avgRating >= 4 ? 'Excellent' : avgRating >= 3 ? 'Good' : avgRating >= 2 ? 'Fair' : 'Needs Improvement'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Image - Show uploaded images in a gallery
  if (type === 'image') {
    const imageResponses = analytics.textResponses || [];

    if (imageResponses.length === 0) {
      return (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600">
            No images uploaded yet.
          </p>
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">
            {analytics.totalAnswers} {analytics.totalAnswers === 1 ? 'Image' : 'Images'} Uploaded
          </p>
        </div>
        <div className="max-h-96 sm:max-h-[32rem] md:max-h-[36rem] overflow-y-auto p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {imageResponses.map((imageUrl, index) => (
              <div key={index} className="group relative">
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all duration-300">
                  <img
                    src={imageUrl}
                    alt={`Response ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-lg">
                  #{index + 1}
                </div>
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  title="View full size"
                >
                  <Eye size={16} className="text-gray-700" />
                </a>
              </div>
            ))}
          </div>
        </div>
        {analytics.totalAnswers > imageResponses.length && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Showing {imageResponses.length} of {analytics.totalAnswers} images
            </p>
          </div>
        )}
      </div>
    );
  }

  // Dropdown, Yes/No, Gender - Show like multiple choice
  if (['dropdown', 'yes-no', 'gender'].includes(type)) {
    const answerDist = analytics.answerDistribution || analytics.optionCounts || {};

    if (answerDist && Object.keys(answerDist).length > 0) {
      const sortedAnswers = Object.entries(answerDist)
        .sort(([, a], [, b]) => b - a);
      const maxCount = Math.max(...Object.values(answerDist));

      return (
        <div className="space-y-3 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto pr-2">
          {sortedAnswers.map(([answer, count]) => {
            const percentage = (count / analytics.totalAnswers) * 100;
            const barWidth = (count / maxCount) * 100;

            return (
              <div key={answer} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700 flex-1 truncate">{answer}</span>
                  <div className="flex items-center gap-3 ml-4">
                    <span className="font-bold text-gray-900">{count}</span>
                    <span className="text-gray-500 min-w-[3rem] text-right">{percentage.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-500 flex items-center px-3"
                    style={{ width: `${barWidth}%` }}
                  >
                    {barWidth > 15 && (
                      <span className="text-xs font-semibold text-white">{count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  }

  // Scale - Show average and distribution
  if (type === 'scale' && analytics.avgRating !== undefined) {
    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
          <div className="text-center">
            <p className="text-sm font-semibold text-blue-700 mb-2">Average Rating</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-5xl font-bold text-blue-600">{analytics.avgRating.toFixed(1)}</span>
              <span className="text-2xl text-blue-400">/10</span>
            </div>
            <div className="mt-4 bg-white/60 rounded-full h-3 overflow-hidden max-w-xs mx-auto">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all"
                style={{ width: `${(analytics.avgRating / 10) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-600 text-center">
          Based on {analytics.totalAnswers} {analytics.totalAnswers === 1 ? 'response' : 'responses'}
        </div>
      </div>
    );
  }

  // Ranking - Show common rankings
  if (type === 'ranking') {
    const textResponses = analytics.textResponses || [];
    if (textResponses.length === 0) {
      return (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600">No responses collected yet.</p>
        </div>
      );
    }
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">
            {analytics.totalAnswers} {analytics.totalAnswers === 1 ? 'Ranking' : 'Rankings'}
          </p>
        </div>
        <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
          {textResponses.map((ranking, index) => (
            <div key={index} className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-blue-600">#{index + 1}</span>
                </div>
                <div className="flex-1">{renderAnswer(ranking, 'ranking')}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Text/Email/Phone/Date/Time/URL/Short-Text/Long-Text - Show scrollable text responses
  if (['text', 'short-text', 'long-text', 'email', 'phone', 'date', 'time', 'url', 'number'].includes(type)) {
    const textResponses = analytics.textResponses || [];

    if (textResponses.length === 0) {
      return (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600">
            No responses collected yet.
          </p>
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">
            {analytics.totalAnswers} {analytics.totalAnswers === 1 ? 'Response' : 'Responses'}
          </p>
        </div>
        <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
          {textResponses.map((response, index) => (
            <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-blue-600">#{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 break-words leading-relaxed">{response}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {analytics.totalAnswers > textResponses.length && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Showing {textResponses.length} of {analytics.totalAnswers} responses
            </p>
          </div>
        )}
      </div>
    );
  }

  // Default fallback
  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
      <p className="text-sm text-gray-600">
        {analytics.totalAnswers} responses collected
      </p>
    </div>
  );
};

const FormAnalytics = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState(null);
  const [runTour, setRunTour] = useState(false);

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const [analyticsResponse, formResponse] = await Promise.all([
        getFormAnalytics(formId).catch(() => ({ success: false })),
        formService.getForm(formId).catch(() => ({ success: false }))
      ]);

      if (analyticsResponse.success) {
        setAnalytics(analyticsResponse.data);
      }

      if (formResponse.success) {
        setForm(formResponse.data);
      }

      if (!analyticsResponse.success && !formResponse.success) {
        toast.error('Failed to load analytics');
        navigate('/dashboard/my-forms');
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => {
    if (formId) {
      loadAnalytics();
    }
  }, [formId, loadAnalytics]);

  // Check if user should see tour (first time viewing analytics with responses)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('fylo_analytics_tour_completed');
    if (!hasSeenTour && !isLoading && analytics?.summary?.totalResponses > 0) {
      // Add delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, analytics]);

  const handleTourComplete = () => {
    localStorage.setItem('fylo_analytics_tour_completed', 'true');
    setRunTour(false);
  };

  const handleStartTour = () => {
    setRunTour(true);
  };

  // Transform analytics data for display
  const formData = analytics ? {
    title: analytics.form?.title || 'Untitled Form',
    responses: analytics.summary?.totalResponses || 0,
    completion: 0, // Calculate from funnel data if available
    avgTime: analytics.summary?.avgCompletionTime
      ? `${Math.floor(analytics.summary.avgCompletionTime / 60)} min`
      : '0 min',
    engagement: analytics.summary?.avgXP
      ? Math.min(10, Math.round((analytics.summary.avgXP / 100) * 10))
      : 0,
    createdAt: analytics.form?.publishedAt || form?.created_at
  } : {
    title: form?.title || 'Loading...',
    responses: 0,
    completion: 0,
    avgTime: '0 min',
    engagement: 0,
    createdAt: form?.created_at
  };

  // Transform response data for chart (last 7 days)
  const responseData = analytics?.responsesByDate
    ? Object.entries(analytics.responsesByDate)
        .map(([date, count]) => ({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          responses: count,
          originalDate: date
        }))
        .sort((a, b) => new Date(a.originalDate) - new Date(b.originalDate))
    : [];

  const responses = analytics?.recentResponses || [];
  const allResponses = responses; // For responses tab, we'll fetch all responses

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'responses', label: 'Responses' }
  ];

  const hasResponses = responses.length > 0;
  const hasResponseData = responseData.length > 0;

  // Empty state content for different scenarios
  const getEmptyStateContent = (context) => {
    const baseContent = {
      overview: {
        icon: BarChart3,
        title: "No analytics data yet",
        description: "Analytics will appear here once you start receiving responses to your form",
        showAction: false
      },
      responses: {
        icon: Inbox,
        title: "No responses yet",
        description: "Share your form to start collecting responses. You'll see them here once people start filling it out.",
        buttonText: "Share Form",
        showAction: true
      },
      chart: {
        icon: TrendingUp,
        title: "No response data",
        description: "Response trends will appear here once you receive form submissions",
        showAction: false
      }
    };

    return baseContent[context] || baseContent.overview;
  };

  const handleShareForm = () => {
    if (!formId) return;

    const shareUrl = `${window.location.origin}/f/${formId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('Form link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // CSV Export Function
  const handleExportCSV = () => {
    if (!form || !responses || responses.length === 0) {
      toast.error('No responses to export');
      return;
    }

    try {
      // Helper function to properly escape and quote CSV values for Excel
      const escapeCSVValue = (value) => {
        if (value === null || value === undefined) return '""';
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      };

      // Prepare CSV headers
      const headers = ['Response #', 'Submitted At', 'Completion Time', 'XP Earned'];

      // Add question headers
      if (form.questions && form.questions.length > 0) {
        form.questions.forEach((question, index) => {
          headers.push(`Q${index + 1}: ${question.text}`);
        });
      }

      // Prepare CSV rows
      const rows = responses.map((response, index) => {
        const row = [
          index + 1,
          new Date(response.submitted_at).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }),
          response.completion_time_seconds
            ? `${Math.floor(response.completion_time_seconds / 60)}:${String(response.completion_time_seconds % 60).padStart(2, '0')}`
            : 'N/A',
          response.total_xp || 0
        ];

        // Add answers for each question
        if (form.questions && form.questions.length > 0) {
          form.questions.forEach((question) => {
            const answer = response.answers?.[question.id];
            let formattedAnswer = '';

            if (answer === null || answer === undefined || answer === '') {
              formattedAnswer = 'No answer';
            } else if (Array.isArray(answer)) {
              // Handle arrays (checkboxes, ranking)
              formattedAnswer = answer.join('; ');
            } else if (typeof answer === 'object') {
              formattedAnswer = JSON.stringify(answer);
            } else {
              formattedAnswer = answer.toString();
            }

            row.push(formattedAnswer);
          });
        }

        return row;
      });

      // Convert to CSV string with proper escaping and Windows line endings
      const csvContent = [
        headers.map(h => escapeCSVValue(h)).join(','),
        ...rows.map(row => row.map(v => escapeCSVValue(v)).join(','))
      ].join('\r\n');

      // Create blob with UTF-8 BOM for Excel compatibility
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${formData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_responses_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success(`Exported ${responses.length} responses to CSV!`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8 p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="xl" variant="orbit" text="Loading analytics..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <button
            onClick={() => navigate('/dashboard/forms')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{formData.title}</h1>
          <p className="text-gray-600 text-lg">Detailed analytics and insights</p>
        </div>
        <div className="flex items-center gap-4">
          {formData.createdAt && (
            <div className="flex items-center gap-2 text-gray-500">
              <Calendar size={18} />
              <span className="text-sm">
                Published: {new Date(formData.createdAt).toLocaleDateString()}
              </span>
            </div>
          )}
          <button
            onClick={handleShareForm}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
            data-tour="share-button"
          >
            <Share2 size={18} />
            Share Form
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 p-2" data-tour="analytics-tabs">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-lightGray'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-tour="metrics-cards">
            <MetricCard
              title="Total Responses"
              value={formData.responses.toString()}
              icon={Users}
              color="blue"
              trend={formData.responses > 0 ? { value: '+100%', positive: true } : null}
            />
            <MetricCard
              title="Completion Rate"
              value={formData.completion.toString()}
              suffix="%"
              icon={CheckCircle}
              color="greenBtn"
              trend={formData.completion > 0 ? { value: `${formData.completion}%`, positive: true } : null}
            />
            <MetricCard
              title="Avg Completion Time"
              value={formData.avgTime}
              icon={Clock}
              color="blue"
            />
            <MetricCard
              title="Engagement Score"
              value={formData.engagement.toString()}
              suffix="/10"
              icon={TrendingUp}
              color="mint"
              trend={formData.engagement > 5 ? { value: 'High', positive: true } : null}
            />
          </div>

          {/* Charts Section */}
          <div className="space-y-6">
            {/* Response Chart - Full Width */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100" data-tour="response-chart">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Response Trends</h3>
                {/* No trend indicator for empty state */}
              </div>
              
              {hasResponseData ? (
                <ResponseChart data={responseData} />
              ) : (
                <div className="h-48 sm:h-64 md:h-80 flex items-center justify-center">
                  <div className="text-center">
                    <TrendingUp size={48} className="mx-auto text-gray-300 mb-4" />
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {getEmptyStateContent('chart').title}
                    </h4>
                    <p className="text-gray-500 max-w-sm">
                      {getEmptyStateContent('chart').description}
                    </p>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Question Analytics Section - Show when there are responses */}
          {formData.responses > 0 && analytics?.questionAnalytics && (
            <div className="space-y-6" data-tour="question-breakdown">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">Question Breakdown</h3>
                <span className="text-sm text-gray-500">{Object.keys(analytics.questionAnalytics).length} questions</span>
              </div>

              <div className="grid grid-cols-1 gap-6 max-h-96 sm:max-h-[40rem] md:max-h-[50rem] overflow-y-auto pr-2">
                {form?.questions && form.questions.map((question, index) => {
                  const questionAnalytics = analytics.questionAnalytics[question.id];
                  if (!questionAnalytics) return null;

                  const responseRate = questionAnalytics.responseRate || 0;
                  const totalAnswers = questionAnalytics.totalAnswers || 0;

                  return (
                    <div
                      key={question.id}
                      className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-blue-200 transition-all duration-300"
                    >
                      {/* Question Header */}
                      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{question.text}</h4>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                                {question.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              {question.required && (
                                <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full font-medium">
                                  Required
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">{totalAnswers}</p>
                          <p className="text-sm text-gray-500">responses</p>
                        </div>
                      </div>

                      {/* Response Rate Bar */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Response Rate</span>
                          <span className="text-sm font-bold text-gray-900">{responseRate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500"
                            style={{ width: `${responseRate}%` }}
                          />
                        </div>
                      </div>

                      {/* Question-Specific Analytics */}
                      {renderQuestionAnalytics(question, questionAnalytics, formData.responses)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State for Overview - Only show when no responses */}
          {formData.responses === 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <div className="max-w-md mx-auto">
                <BarChart3 size={64} className="mx-auto text-gray-300 mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {getEmptyStateContent('overview').title}
                </h3>
                <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                  {getEmptyStateContent('overview').description}
                </p>
                <button
                  onClick={handleShareForm}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-blue text-white rounded-xl hover:bg-blue/90 transition-all duration-300 border-0 font-semibold text-lg"
                >
                  <Users size={20} />
                  Share Form to Collect Responses
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'responses' && (
        <div className="space-y-6">
          {/* Response Stats Bar */}
          {hasResponses && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white border border-blue-500">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Total Responses</p>
                  <p className="text-3xl font-bold">{formData.responses}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Avg XP Earned</p>
                  <p className="text-3xl font-bold">
                    {analytics?.summary?.avgXP
                      ? Math.round(analytics.summary.avgXP)
                      : responses.length > 0 && responses.some(r => r.total_xp)
                        ? Math.round(responses.reduce((sum, r) => sum + (r.total_xp || 0), 0) / responses.length)
                        : 0}
                  </p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Avg Time</p>
                  <p className="text-3xl font-bold">{formData.avgTime}</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Latest Response</p>
                  <p className="text-xl font-bold">
                    {responses.length > 0
                      ? getTimeAgo(new Date([...responses].sort((a, b) =>
                          new Date(b.submitted_at) - new Date(a.submitted_at)
                        )[0].submitted_at))
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Response count and Export */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {formData.responses} Response{formData.responses !== 1 ? 's' : ''}
            </h3>
            {hasResponses && (
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
                data-tour="export-csv"
              >
                <Download size={18} />
                Export to Excel
              </button>
            )}
          </div>

          {/* Responses List or Empty State */}
          {hasResponses ? (
            <div className="grid grid-cols-1 gap-6 max-h-96 sm:max-h-[40rem] md:max-h-[50rem] overflow-y-auto pr-2" data-tour="response-list">
              {allResponses.map((response, index) => {
                const submittedDate = new Date(response.submitted_at);
                const timeAgo = getTimeAgo(submittedDate);

                return (
                  <div
                    key={response.id || index}
                    className="bg-white rounded-2xl border-2 border-gray-100 hover:border-blue-300  transition-all duration-300 overflow-hidden"
                  >
                    {/* Response Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 px-6 py-4 border-b border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center border border-blue-500">
                            <span className="text-white font-bold text-lg">#{index + 1}</span>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg">Response #{index + 1}</h4>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Calendar size={14} />
                                {submittedDate.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <span className="text-gray-300">•</span>
                              <p className="text-sm text-gray-500">{timeAgo}</p>
                            </div>
                          </div>
                        </div>

                        {/* Stats Badges */}
                        <div className="flex items-center gap-3">
                          {response.total_xp > 0 && (
                            <div className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-bold border border-blue-500 flex items-center gap-2">
                              <Zap size={16} fill="currentColor" />
                              {response.total_xp} XP
                            </div>
                          )}
                          {response.completion_time_seconds && (
                            <div className="px-4 py-2 bg-white border-2 border-blue-200 text-blue-700 rounded-xl text-sm font-semibold flex items-center gap-2">
                              <Clock size={16} />
                              {Math.floor(response.completion_time_seconds / 60)}:{String(response.completion_time_seconds % 60).padStart(2, '0')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Answers Section */}
                    <div className="p-6">
                      <div className="grid grid-cols-1 gap-5 max-h-[500px] overflow-y-auto pr-2">
                        {form?.questions && form.questions.map((question, qIndex) => {
                          const answer = response.answers?.[question.id];
                          if (!answer && answer !== 0) return null;

                          return (
                            <div
                              key={question.id}
                              className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:bg-blue-50/30 hover:border-blue-200 transition-all duration-200"
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                                  {qIndex + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-700 mb-2 leading-relaxed">
                                    {question.text}
                                  </p>
                                  <div className="bg-white rounded-lg px-4 py-3 border border-gray-200">
                                    {renderAnswer(answer, question.type)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Response Metadata Footer */}
                      {response.metadata && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Eye size={14} />
                              Submitted via {response.metadata.userAgent?.includes('Mobile') ? 'Mobile' : 'Desktop'}
                            </span>
                            {response.metadata.screenResolution && (
                              <>
                                <span>•</span>
                                <span>{response.metadata.screenResolution}</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <div className="max-w-md mx-auto">
                <Inbox size={64} className="mx-auto text-gray-300 mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {getEmptyStateContent('responses').title}
                </h3>
                <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                  {getEmptyStateContent('responses').description}
                </p>
                <button
                  onClick={handleShareForm}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-blue text-white rounded-xl hover:bg-blue/90 transition-all duration-300 border-0 font-semibold text-lg"
                >
                  <Users size={20} />
                  {getEmptyStateContent('responses').buttonText}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Button - Start Tour */}
      <button
        onClick={handleStartTour}
        className="fixed bottom-4 right-4 w-12 h-12 bg-white text-blue-600 rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center z-40 shadow-lg"
        title="Start Tour"
        aria-label="Start Tour"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {/* Product Tour */}
      <FormAnalyticsTour run={runTour} onComplete={handleTourComplete} />
    </div>
  );
};

export default FormAnalytics;